import React, { useMemo, useRef } from "react";
import Plot from "../../graphViewV2/charts/PlotlyChart";
import { plotlyAxisBase, plotlyAxisTitle } from "../../graphViewV2/charts/plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "../../graphViewV2/charts/plotlyLegendLayout";
import {
  GraphV2FullscreenInlineRow,
  POND_STAT_DESKTOP_HEIGHT,
  POND_STAT_MOBILE_HEIGHT,
  useGraphV2PlotHeight,
} from "../../graphViewV2/ChartFullscreenShell";
import { getPlotlyLegendStyle, sortSpeciesForWeightLengthLegend } from "../../graphView/colors";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import { formatDisplayDate } from "../../../../utils/formatDisplayDate";

const METRICS = [
  { key: "psdQ", label: "PSD-Q" },
  { key: "psdP", label: "PSD-P" },
  { key: "psdM", label: "PSD-M" },
  { key: "psdT", label: "PSD-T" },
];

function pickEvenlySpacedIndices(n, maxTicks) {
  if (n <= 0) return [];
  if (n <= maxTicks) return Array.from({ length: n }, (_, i) => i);
  const idx = new Set([0, n - 1]);
  const inner = maxTicks - idx.size;
  for (let k = 1; k <= inner; k++) {
    idx.add(Math.round((k * (n - 1)) / (inner + 1)));
  }
  return [...idx].sort((a, b) => a - b);
}

/** @param {Array<{ dateLabel: string, dateMs: number, species: string, psdQ?: number|null, ... }>} rows */
export default function PondStatPsdVsDateChart({ isMobile, rows }) {
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);

  const { categoryarray, msToCategoryLabel } = useMemo(() => {
    const msToLabel = new Map();
    (rows || []).forEach((r) => {
      const ms = r.dateMs;
      if (ms == null || Number.isNaN(ms)) return;
      const lab = String(r.dateLabel ?? "").trim();
      if (!msToLabel.has(ms)) {
        msToLabel.set(ms, lab || formatDisplayDate(ms));
      }
    });
    const sorted = [...msToLabel.keys()].sort((a, b) => a - b);
    return {
      categoryarray: sorted.map((ms) => msToLabel.get(ms)),
      msToCategoryLabel: new Map(sorted.map((ms) => [ms, msToLabel.get(ms)])),
    };
  }, [rows]);

  const speciesOrder = useMemo(() => {
    const u = [...new Set((rows || []).map((r) => r.species || "Unknown"))];
    return sortSpeciesForWeightLengthLegend(u);
  }, [rows]);

  const xAxisTickLayout = useMemo(() => {
    const n = categoryarray.length;
    if (n === 0) {
      return { tickvals: null, ticktext: null, marginBottom: isMobile ? 100 : 90, tickFontSize: 14 };
    }
    const labelCap = isMobile ? 10 : 16;
    const ix = pickEvenlySpacedIndices(n, labelCap);
    const displayLabels = ix.map((i) => categoryarray[i]);
    const marginBottom = isMobile
      ? Math.min(200, 100 + Math.min(displayLabels.length * 3, 72))
      : Math.min(200, 96 + Math.min(displayLabels.length * 3, 64));
    const tickFontSize =
      n > 14 ? (isMobile ? 9 : 10) : n > 9 ? (isMobile ? 11 : 12) : isMobile ? 14 : 16;
    return { tickvals: displayLabels, ticktext: displayLabels, marginBottom, tickFontSize };
  }, [categoryarray, isMobile]);

  const traces = useMemo(() => {
    const catX = (r) => {
      const ms = r.dateMs;
      return msToCategoryLabel.get(ms) ?? String(r.dateLabel ?? "");
    };
    const sortedRows = [...(rows || [])].sort((a, b) => a.dateMs - b.dateMs || a.species.localeCompare(b.species));
    const legendOrder = speciesOrder.flatMap((sp) =>
      METRICS.map((m) => `${sp} · ${m.label}`)
    );
    const t = [];
    speciesOrder.forEach((sp) => {
      const spRows = sortedRows.filter((r) => (r.species || "Unknown") === sp);
      METRICS.forEach((m) => {
        const traceName = `${sp} · ${m.label}`;
        const ys = spRows.map((r) => {
          const v = r[m.key];
          return v == null || !Number.isFinite(Number(v)) ? null : Number(v);
        });
        if (!ys.some((y) => y != null)) return;
        const { color, symbol } = getPlotlyLegendStyle(traceName, legendOrder);
        t.push({
          type: "scatter",
          mode: "lines+markers",
          name: traceName,
          x: spRows.map(catX),
          y: ys,
          line: {
            color,
            width: 2,
            dash: m.key === "psdQ" ? "solid" : m.key === "psdP" ? "dash" : m.key === "psdM" ? "dot" : "dashdot",
          },
          marker: { color, size: 7, symbol, line: { color: "white", width: 1 } },
          connectgaps: false,
        });
      });
    });
    return t;
  }, [rows, speciesOrder, msToCategoryLabel]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile, { itemsPerRow: 4 }),
    [traces.length, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: POND_STAT_MOBILE_HEIGHT,
    desktopDefault: POND_STAT_DESKTOP_HEIGHT,
    legendExtra: legendLayout.extraHeight || 0,
  });

  if (!rows || rows.length === 0) {
    return (
      <p className="text-muted text-center py-6 mb-0">
        No PSD data (load sampling with fish and PSD thresholds).
      </p>
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GraphV2FullscreenInlineRow />
      <Plot
          ref={plotRef}
          data={traces}
          layout={{
            autosize: true,
            height: plotHeight,
            margin: { t: legendLayout.marginTop, r: 40, b: xAxisTickLayout.marginBottom, l: 64 },
            paper_bgcolor: "white",
            plot_bgcolor: "white",
            xaxis: {
              type: "category",
              categoryorder: "array",
              categoryarray,
              title: plotlyAxisTitle("Date", isMobile),
              ...(xAxisTickLayout.tickvals && xAxisTickLayout.tickvals.length
                ? {
                    tickmode: "array",
                    tickvals: xAxisTickLayout.tickvals,
                    ticktext: xAxisTickLayout.ticktext,
                  }
                : {}),
              automargin: true,
              ...plotlyAxisBase(isMobile, "default"),
              tickfont: {
                ...plotlyAxisBase(isMobile, "default").tickfont,
                size: xAxisTickLayout.tickFontSize,
              },
            },
            yaxis: {
              title: plotlyAxisTitle("PSD (%)", isMobile),
              range: [0, 105],
              ...plotlyAxisBase(isMobile, "default"),
            },
            legend: legendLayout.legend,
            hovermode: "closest",
          }}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: "100%" }}
          useResizeHandler
        />
    </div>
  );
}
